import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { i18n, withCount } from '../lib/i18n/catalog'
import { SOURCE_LANGUAGE } from '../lib/i18n/languages'
import { useWords } from '../lib/i18n/useWords'
import type { StaffKey, StaffLanguage, Vars } from '../lib/staffText'

// The reader's language, handed down once rather than threaded through every
// component; English outside a provider. The way to change it travels with it:
// the top-bar menu is the one place a language is chosen.
interface StaffLanguageValue {
  language: StaffLanguage
  /** The languages this reader may choose from: released, then drafts. */
  languages: readonly StaffLanguage[]
  /** Of those, the unreleased ones a reviewer reads (marked Draft). */
  drafts: readonly StaffLanguage[]
  choose?: (lang: StaffLanguage) => Promise<void>
}

const StaffLanguageContext = createContext<StaffLanguageValue>({ language: SOURCE_LANGUAGE, languages: [SOURCE_LANGUAGE], drafts: [] })

const NO_DRAFTS: readonly StaffLanguage[] = []

export function StaffLanguageProvider({
  language,
  languages = [SOURCE_LANGUAGE],
  drafts = NO_DRAFTS,
  onChoose,
  children,
}: {
  language: StaffLanguage
  languages?: readonly StaffLanguage[]
  drafts?: readonly StaffLanguage[]
  onChoose?: (lang: StaffLanguage) => Promise<void>
  children: ReactNode
}) {
  useWords(language)
  return <StaffLanguageContext.Provider value={{ language, languages, drafts, choose: onChoose }}>{children}</StaffLanguageContext.Provider>
}

/** `say(key, vars?)` in the reader's language (i18next), and the language itself for the lib functions that take one. */
export function useStaffText(): { lang: StaffLanguage; say: (key: StaffKey, vars?: Vars) => string } {
  const { language: lang } = useContext(StaffLanguageContext)
  // Re-renders when a language file arrives or the organisation's words change.
  const options = useMemo(() => ({ i18n, lng: lang, useSuspense: false, bindI18n: false as const, bindI18nStore: 'added removed' }), [lang])
  const { t } = useTranslation(undefined, options)
  const say = useCallback((key: StaffKey, vars?: Vars) => t(key, { replace: withCount(vars) }) as string, [t])
  return { lang, say }
}

/** The languages the menu offers and the way to choose one; undefined while there is nothing to choose between. */
export function useLanguageChoice():
  | { languages: readonly StaffLanguage[]; drafts: readonly StaffLanguage[]; choose: (lang: StaffLanguage) => Promise<void> }
  | undefined {
  const { languages, drafts, choose } = useContext(StaffLanguageContext)
  return choose && languages.length > 1 ? { languages, drafts, choose } : undefined
}
